import * as B from '@blueprintjs/core';
import classNames from 'classnames';
import React from 'react';
import Markdown from 'react-markdown';

import {LoadingEllipsis} from 'app/components/Text/LoadingEllipses';
import colors from 'app/utils/colorUtils';

import * as cs from './styles.styl';
import {AgentResponse} from './utils';

type StepDialogProps = {
  stepTitle: string;
  agentLines: AgentResponse[];
  isStreaming: boolean;
  isQueued?: boolean;
  defaultOpen?: boolean;
  autoScroll?: boolean;
  className?: string;
  collapsible?: boolean;
  isCollapsed?: boolean;
};

// Custom hook for auto-scrolling to the bottom using a sentinel element
const useAutoScrollToBottom = (enabled: boolean, dependencies: any[]) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    if (!enabled) return;
    const el = scrollRef.current;
    if (!el) return;
    try {
      scrollRef.current?.scrollTo({
        top: scrollRef.current?.scrollHeight,
      });
    } catch (_) {
      // if this breaks don't barf
    }
  }, [enabled, ...dependencies]);

  return scrollRef;
};

// Custom hook to check if the scroll exists on the element
// https://medium.com/@danil_chepelev/detect-scroll-bar-existence-in-react-de112065153
export function useCheckScrollExistence(ref: React.RefObject<HTMLDivElement>) {
  const [doesScrollExist, setDoesScrollExist] = React.useState(false);

  React.useLayoutEffect(() => {
    const contentWrapper = ref?.current;

    const updateScrollState = () => {
      contentWrapper &&
        setDoesScrollExist(contentWrapper.scrollHeight > contentWrapper.clientHeight);
    };

    updateScrollState();

    const observer = new MutationObserver(() => {
      updateScrollState();
    });

    contentWrapper &&
      observer.observe(contentWrapper, {
        attributes: true,
        childList: true,
        subtree: true,
      });

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return doesScrollExist;
}

export const StepDialog: React.FC<StepDialogProps> = ({
  stepTitle,
  agentLines,
  isStreaming,
  isQueued,
  autoScroll = false,
  className,
  collapsible = true,
  defaultOpen = false,
}) => {
  const hasText = agentLines.some((line) => line.type === 'text');
  const [isOpen, setIsOpen] = React.useState<boolean>(defaultOpen || false);
  const [allowAutoScroll, setAllowAutoScroll] = React.useState<boolean>(autoScroll);

  // Use the auto-scroll hook (scroll when content changes or section opens)
  const scrollRef = useAutoScrollToBottom(allowAutoScroll, [agentLines, isOpen, allowAutoScroll]);
  const doesScrollExist = useCheckScrollExistence(scrollRef);

  // The function fo the button is to re-enable auto-scroll after the user has manually scrolled up while streaming
  const showScrollButton = isStreaming && doesScrollExist && !allowAutoScroll;

  React.useEffect(() => {
    if (!isStreaming) {
      setTimeout(() => {
        setIsOpen(false);
      }, 1000);
    }
  }, [isStreaming]);

  return (
    <B.Section
      collapsible={collapsible}
      collapseProps={{
        isOpen: isOpen,
        onToggle: () => {
          setIsOpen(!isOpen);
        },
      }}
      className={classNames({
        [cs.activeLensAgent]: isStreaming,
      })}
      title={
        <span className={cs.sectionHeader}>
          <B.Icon
            icon={isQueued ? 'time' : isStreaming ? 'clean' : 'tick'}
            color={colors.purple}
            className={isStreaming ? cs.thinkingDots : ''}
          />
          <B.H6 className={cs.sectionTitle}>{stepTitle}</B.H6>
        </span>
      }
    >
      <B.SectionCard
        className={classNames(cs.codeText, className, {
          [cs.streamingGradient]: isStreaming,
        })}
        onWheel={() => setAllowAutoScroll(false)}
        ref={scrollRef}
      >
        {hasText && (
          <Markdown>
            {agentLines
              .filter((line) => line.type === 'text')
              .map((line) => line.label)
              .join('\n') + '\n'}
          </Markdown>
        )}
        {isStreaming && <LoadingEllipsis />}
        {showScrollButton && (
          <B.Button
            style={{
              position: 'absolute',
              bottom: '3rem',
              right: '4rem',
              backgroundColor: 'white',
            }}
            outlined
            icon="caret-down"
            onClick={() => {
              scrollRef.current?.scrollTo({
                top: scrollRef.current?.scrollHeight,
                behavior: 'smooth',
              });
              setAllowAutoScroll(true);
            }}
          />
        )}
      </B.SectionCard>
    </B.Section>
  );
};
